import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { useQuery } from '@tanstack/react-query';
import UpButton from '../../components/Common/UpButton';
import AppPage from './AppPage';
import { loadStripeJs } from '../../helpers/stripeHelper';
import {
  createSetupIntent,
  getSignupErrorMessage,
  getSignupRequestId,
} from '../../api/signup';

function CardView({ token, state }) {
  const mountRef = useRef(null);
  const stripeRef = useRef(null);
  const elementsRef = useRef(null);
  const [elementReady, setElementReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Idempotent on the server — the Stripe customer is created on first call.
  const {
    data: intent,
    error: intentError,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['signup-setup-intent', token],
    queryFn: () => createSetupIntent(token),
    retry: 1,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // A 200 with missing Stripe keys must offer the same retry path as a
  // failed request — without this the form stays hidden with no way
  // forward but a browser refresh (CodeRabbit catch).
  const hasInvalidIntent = Boolean(
    intent && (!intent.client_secret || !intent.publishable_key),
  );

  useEffect(() => {
    if (!intent || !mountRef.current) {
      return undefined;
    }

    if (!intent.client_secret || !intent.publishable_key) {
      // don't log the intent itself — the client secret must stay out of
      // console capture (Sentry)
      console.error('setup-intent response is missing Stripe keys', {
        hasClientSecret: Boolean(intent.client_secret),
        hasPublishableKey: Boolean(intent.publishable_key),
      });
      return undefined;
    }

    setErrorMessage('');

    let paymentElement = null;
    let cancelled = false;

    loadStripeJs()
      .then(Stripe => {
        if (cancelled) {
          return;
        }

        const stripe = Stripe(intent.publishable_key);
        const elements = stripe.elements({
          clientSecret: intent.client_secret,
        });
        stripeRef.current = stripe;
        elementsRef.current = elements;

        // The element's country guess is IP-based (demos from India showed
        // India) — seed it from the referral instead; v1 is US-only.
        paymentElement = elements.create('payment', {
          defaultValues: {
            billingDetails: {
              email: state?.identity?.billing_contact_email || state?.email,
              address: { country: state?.country || 'US' },
            },
          },
        });
        paymentElement.on('ready', () => setElementReady(true));
        paymentElement.mount(mountRef.current);
      })
      .catch(() =>
        setErrorMessage(
          'We could not load the secure payment form. Please refresh the page and try again.',
        ),
      );

    return () => {
      cancelled = true;
      if (paymentElement) {
        paymentElement.destroy();
      }
    };
  }, [intent, state]);

  async function handleSubmit(e) {
    e.preventDefault();

    if (!stripeRef.current || !elementsRef.current || submitting) {
      return;
    }

    setSubmitting(true);
    setErrorMessage('');

    try {
      const { error } = await stripeRef.current.confirmSetup({
        elements: elementsRef.current,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(
          error.message || 'We could not save this card. Please try again.',
        );
        return;
      }

      // Success is confirmed server-side — the wizard polls status until the
      // vaulted payment method shows up, then moves to done.
      setConfirmed(true);
    } catch (err) {
      setErrorMessage('We could not save this card. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const waiting = confirmed;

  return (
    <AppPage step={state?.step} loading={isLoading || waiting}>
      <h1 className="signup-title">Add a payment method</h1>
      <p className="signup-sub">
        {state?.business || 'Your brand'}&apos;s card is only charged as
        per your signed order form.
      </p>
      {(intentError || hasInvalidIntent) && (
        <div className="text-sm text-up-clr-semantic-red break-words mb-5">
          {intentError
            ? getSignupErrorMessage(
                intentError,
                'We could not start the payment setup.',
              )
            : 'The payment form isn’t available right now. Please try again.'}
          {getSignupRequestId(intentError) && (
            <span className="block text-xs text-up-clr-light-grey mt-1">
              Reference: {getSignupRequestId(intentError)}
            </span>
          )}
          <UpButton
            variant="link"
            wrapperClass="w-max mt-2"
            onClick={() => refetch()}>
            Try again
          </UpButton>
        </div>
      )}
      {waiting ? (
        <p className="text-sm text-up-clr-dark-grey">
          Card saved — confirming with our billing system…
        </p>
      ) : (
        <form
          onSubmit={handleSubmit}
          className={intentError || hasInvalidIntent ? 'hidden' : ''}>
          <div
            ref={mountRef}
            className={cx({
              'signup-payment-element mb-5': !errorMessage || elementReady,
            })}
          />
          {errorMessage && (
            <div className="text-sm text-up-clr-semantic-red break-words mb-5">
              {errorMessage}
            </div>
          )}
          <p className="payment-disclaimer">
            By providing your card information, you allow UrbanPiper Inc. to
            charge your card for future payments in accordance with their
            terms.
          </p>
          <UpButton type="submit" disabled={!elementReady || submitting}>
            {submitting ? 'Saving card…' : 'Save card'}
          </UpButton>
          <p className="signup-footnote">
            Card details are handled securely by Stripe.
          </p>
        </form>
      )}
    </AppPage>
  );
}

export default CardView;
